import type { CustomFieldDef } from "#app/components/custom-fields/types";
import type { FilterFieldDef, OperatorDef } from "./types";
import {
  contains,
  notContains,
  eq,
  ne,
  gt,
  gte,
  lt,
  lte,
  hasSome,
  hasNone,
  isNull,
  isNotNull,
} from "./operators";

// ---------------------------------------------------------------------------
// Custom-field filter fields — one FilterFieldDef per workspace custom field,
// keyed `values.<customFieldId>` so buildWhereFromState nests the condition
// under IssueWhereInput.values. Operator sets are per field type; the backend
// counterpart is IssuesService.applyCustomFieldSpec — keep the two vocabularies
// in sync. `isNull`/`isNotNull` serialize to the backend's `exists` operator
// (a jsonb value has no SQL NULL to compare against) — see processFilter.
// ---------------------------------------------------------------------------

const CUSTOM_FIELD_FILTER_PREFIX = "values.";

export function isCustomFieldFilterField(field: string): boolean {
  return field.startsWith(CUSTOM_FIELD_FILTER_PREFIX);
}

const IS_SET: OperatorDef[] = [isNotNull("Is set"), isNull("Is not set")];

// Option/id array types — "Is" means "includes any of the picked values".
const OPTION_OPERATORS: OperatorDef[] = [hasSome("Is"), hasNone("Is not"), ...IS_SET];

const TEXT_OPERATORS: OperatorDef[] = [
  contains("Contains"),
  notContains("Does not contain"),
  eq("Is"),
  ne("Is not"),
  ...IS_SET,
];

// Paragraph values are stored HTML — exact equality is meaningless.
const PARAGRAPH_OPERATORS: OperatorDef[] = [
  contains("Contains"),
  notContains("Does not contain"),
  ...IS_SET,
];

const NUMBER_OPERATORS: OperatorDef[] = [
  eq("Equals"),
  ne("Not equals"),
  gt("Greater than"),
  gte("Greater than or equal"),
  lt("Less than"),
  lte("Less than or equal"),
  ...IS_SET,
];

const DATE_OPERATORS: OperatorDef[] = [
  gte("Is on or after"),
  lte("Is on or before"),
  gt("Is after"),
  lt("Is before"),
  ...IS_SET,
];

const CHECKBOX_OPERATORS: OperatorDef[] = [eq("Is"), ...IS_SET];

// Types whose values can't be compared meaningfully (composite objects,
// media arrays, bare times) — presence checks only.
const PRESENCE_OPERATORS: OperatorDef[] = IS_SET;

const OPERATORS_BY_TYPE: Record<string, OperatorDef[]> = {
  labels: OPTION_OPERATORS,
  select: OPTION_OPERATORS,
  member: OPTION_OPERATORS,
  text: TEXT_OPERATORS,
  email: TEXT_OPERATORS,
  phone: PRESENCE_OPERATORS,
  url: TEXT_OPERATORS,
  paragraph: PARAGRAPH_OPERATORS,
  number: NUMBER_OPERATORS,
  money: NUMBER_OPERATORS,
  date: DATE_OPERATORS,
  time: PRESENCE_OPERATORS,
  checkbox: CHECKBOX_OPERATORS,
  address: PRESENCE_OPERATORS,
  photos: PRESENCE_OPERATORS,
  files: PRESENCE_OPERATORS,
  region: PRESENCE_OPERATORS,
  // `formula` has no entry on purpose: its value is computed and nothing
  // stores one yet, so even a presence check has nothing to read. The type
  // is unfilterable until the evaluator ships.
};

/** Build the advanced-filter field def for one custom field (null = unfilterable type). */
export function buildCustomFieldFilterDef(field: CustomFieldDef): FilterFieldDef | null {
  const operators = OPERATORS_BY_TYPE[field.type];
  if (!operators) return null;
  return {
    field: `${CUSTOM_FIELD_FILTER_PREFIX}${field.customFieldId}`,
    label: field.name,
    type: "custom-field",
    operators,
    customField: field,
  };
}
